import _ from 'lodash'
import React from 'react'

import apis from 'browser/app/models/apis'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Popover } from 'browser/components/atomic-elements/atoms/popover/popover'
import { MultiSelectList } from 'browser/components/atomic-elements/atoms/select/multi-select-list'
import { HelpBlock } from 'browser/components/atomic-elements/atoms/help-block/help-block'
import { ButtonGroupSelect } from 'browser/components/atomic-elements/atoms/select/button-group-select/button-group-select'
import { NegatableFilter } from '../negation-filter'
import { DEBOUNCE_TIMEOUT } from 'browser/app/utils/utils'

export interface IMultiSelectFilterPopoverProps extends IBaseProps {
  activeFilters: any[]
  entitySchema: object
  filter: any
  onChange: (value: any, ...args: unknown[]) => void
  onClear: () => void
  onResize?: () => void
  optionKey?: string
  optionLabeler: (filter: any, option: any, entitySchema: any) => string
  FilterOptionsCollection: any
}

type Mode = 'original' | 'exists' | 'notExists'

const MODE_OPTIONS = [
  { label: 'Filter Individually', value: 'original' },
  { label: 'Any Value', value: 'exists'},
  { label: 'Missing Value', value: 'notExists' },
]

export class MultiSelectFilterPopover extends React.Component<IMultiSelectFilterPopoverProps, any> {
  public static defaultProps: Pick<IMultiSelectFilterPopoverProps, 'optionKey'> = {
    optionKey: 'entityId',
  }

  public collection: any
  public list: any

  constructor(props: IMultiSelectFilterPopoverProps) {
    super(props)
    const baseFilter = _.cloneDeep(props.filter)
    baseFilter.values = _.get(baseFilter, 'values', [])

    this.state = {
      filter: baseFilter,
      isLoading: true,
      isSearching: false,
      options: [],
      originalFilterType: baseFilter.fromType ?? baseFilter.type,
      mode: baseFilter.fromType ? baseFilter.type : 'original',
    }

    const { activeFilters, entitySchema, FilterOptionsCollection } = props
    this.collection = new FilterOptionsCollection(apis, activeFilters, entitySchema, baseFilter)

    this.performSearch = _.debounce(this.performSearch.bind(this), DEBOUNCE_TIMEOUT)
  }

  public componentDidMount() {
    const { onResize } = this.props
    this.performSearch(onResize)
  }

  public componentDidUpdate(prevProps: IMultiSelectFilterPopoverProps) {
    if (!_.isEqual(prevProps.filter, this.props.filter)) {
      const next = _.cloneDeep(this.props.filter)
      next.values = _.get(next, 'values', [])
      this.setState({
        filter: next,
      })
    }
  }

  public componentWillUnmount() {
    this.collection?.cancel?.()
  }

  private handleNegation = (filter: unknown, ...args: unknown[]) => {
    this.setState({ filter })
    this.props.onChange(filter, ...args)
  }

  public render() {
    const { originalFilterType } = this.state

    const mode = this.state.mode ?? 'original'
    const effectiveMode =
    mode === 'original' && (originalFilterType === 'exists' || originalFilterType === 'notExists')
      ? originalFilterType
      : mode
    return (
      <Popover className="collapse">
        {this.renderDynamicFilterWarning()}
        {this.renderModeToggle(effectiveMode)}
        {effectiveMode === 'exists' || effectiveMode === 'notExists'
          ? this.renderExistsHelpText(effectiveMode)
          : this.renderValueList()}
      </Popover>
    )
  }

  private renderModeToggle(mode: Mode) {
    const { originalFilterType } = this.state
    const filteredOptions = (originalFilterType === 'exists' || originalFilterType === 'notExists') ? MODE_OPTIONS.filter(
      (opt) => opt.value !== 'original') : MODE_OPTIONS
    return (
      <div className="u-innerBumperLeft u-innerBumperBottom u-innerBumperTop">
        <ButtonGroupSelect
          value={mode}
          options={filteredOptions}
          onChange={(val) => this.handleModeChange(val as Mode)}
        />
      </div>
    )
  }

  private renderExistsHelpText(mode: Extract<Mode, 'exists' | 'notExists'>) {
    const HELP_TEXT = {
      exists: {
        title: 'Any value will match',
        desc:  'All records containing a value for this field are included in the results.',
      },
      notExists: {
        title: 'Only missing values will match',
        desc:  'All records with no value for this field are included in the results.',
      },
    }
    const existsHelpText = HELP_TEXT[mode]
    return (
      <div>
        <HelpBlock
          helpText={existsHelpText?.title}
          style={{
            color: '#0e0e0eff',
            margin: 0,
            padding: 10,
            fontSize: 14,
            textAlign: 'center',
          }}
        />
        <HelpBlock
          helpText={existsHelpText?.desc}
          style={{
            color: '#0e0e0eff',
            margin: 0,
            padding: 10,
            fontSize: 14,
            textAlign: 'center',
          }}
        />
      </div>
    )
  }

  private renderValueList() {
    const { optionKey } = this.props
    const { filter, isLoading, isSearching, options } = this.state
    return (
      <>
        <div className="u-innerBumperBottom u-innerBumperLeft">
          <NegatableFilter filter={filter} onChange={this.handleNegation} />
        </div>
        <MultiSelectList
          getOptionLabel={this.handleGetOptionLabel}
          hasNext={this.collection.hasNext()}
          isLoading={isLoading}
          isSearching={isSearching}
          onChange={this.handleSelectionChange}
          onLoadMore={this.handleLoadMoreData}
          onSearch={this.handleQuery}
          optionKey={optionKey}
          options={options}
          ref={(ref) => { this.list = ref }}
          searchQuery={this.collection.query}
          selection={filter.values}
        />
      </>
    )
  }

  private renderDynamicFilterWarning() {
    const { filter } = this.props
    if (filter.__dynamicFilter) {
      return (
        <div className="u-innerBumperLeft u-innerBumperRight u-innerBumperTop">
          <HelpBlock
            helpText="⚠️ Manual selections will override the dynamic filter. To restore the default dynamic filter, refresh your browser without saving."
            className="wrap"
            style={{
              color: '#0e0e0eff',
              marginTop: 0,
              display: 'flex'
            }}
          />
        </div>
      )
    }
  }


  private performSearch(onResize?: () => void) {
    this.collection.find(true).then((content: unknown) => {
      this.setState({
        isLoading: false,
        isSearching: false,
        options: content,
      }, onResize)
      this.list?.forceUpdateList()
    })
  }

  private handleGetOptionLabel = (option: { data?: unknown }) => {
    const { optionLabeler, entitySchema } = this.props
    const { filter } = this.state
    return optionLabeler(filter, option.data, entitySchema)
  }

  private handleLoadMoreData = (offset: number) => {
    this.collection.next().then((content: unknown) => {
      this.setState({ options: content })
    })
  }

  private handleQuery = (query: string) => {
    this.collection.query = query
    this.setState({ isSearching: true })
    this.performSearch()
  }

  private handleModeChange = (mode: Mode) => {
    const { filter: curFilter, originalFilterType } = this.state

    if (mode === 'original') {
      const nextFilter = { ...curFilter, type: originalFilterType, fromType: undefined, values: [], value: undefined }
      this.setState({ mode, filter: nextFilter })
      this.props.onClear?.()
      return
    }

    const nextFilter = {
      ...curFilter,
      type: mode,
      fromType: originalFilterType,
      values: undefined,
      value: undefined,
    }
    this.setState({ mode, filter: nextFilter })
    this.props.onChange(nextFilter, false)
  }

  private handleSelectionChange = (selection: unknown) => {
    const { filter } = this.state
    filter.values = selection
    // If the user manually changes a previously expression-driven filter,
    // drop the expression metadata so it no longer gets restored.
    if (filter.__dynamicFilter) {
      delete filter.__dynamicFilter
    }
    if (_.isEmpty(selection)) {
      this.props.onClear()
    } else {
      this.props.onChange(filter)
    }
  }
}
